import { ChangeDetectorRef, inject, Injectable, NgZone, signal } from '@angular/core';
import { InputComponent } from 'vcp-common-ui-lib';
import { INCORRECT_PASSWORD, PasswordPrompt } from 'ngx-extended-pdf-viewer';

@Injectable()
export class PdfViewerPasswordPromptService implements PasswordPrompt {
    private readonly _ngZone: NgZone = inject(NgZone);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    readonly showDialog = signal(false);
    readonly passwordValue = signal('');
    readonly passwordInvalid = signal(false);

    private updateCallback: ((password: string) => void) | null = null;
    private passwordInput: InputComponent | null = null;
    private invalidMessage: string = '';

    registerPasswordInput(input: InputComponent, invalidMessage: string): void {
        this.passwordInput = input;
        this.invalidMessage = invalidMessage;
    }

    setUpdateCallback(updateCallback: (password: string) => void, reason: 1 | 2): void {
        this._ngZone.run(() => {
            this.updateCallback = updateCallback;
            this.passwordInvalid.set(reason === INCORRECT_PASSWORD);

            if (reason === INCORRECT_PASSWORD) {
                this.passwordValue.set('');
                this.showDialog.set(true);
                this.showPasswordInputError();
            }
        });
    }

    open(): void {
        this._ngZone.run(() => {
            this.passwordValue.set('');
            this.showDialog.set(true);

            if (this.passwordInvalid()) {
                this.showPasswordInputError();
                return;
            }

            this.clearPasswordInputError();
        });
    }

    close(): void {
        this._ngZone.run(() => {
            this.showDialog.set(false);
            this.passwordValue.set('');
            this.passwordInvalid.set(false);
            this.clearPasswordInputError();
        });
    }

    setPasswordValue(value: string): void {
        this.passwordValue.set(value);

        if (this.passwordInvalid()) {
            this.passwordInvalid.set(false);
            this.clearPasswordInputError();
        }
    }

    submit(): void {
        const password = this.passwordValue().trim();

        if (!password || !this.updateCallback) {
            return;
        }

        const callback = this.updateCallback;
        this.updateCallback = null;
        callback(password);
        this.passwordValue.set('');
    }

    cancel(): void {
        if (!this.updateCallback) {
            this.close();
            return;
        }

        const callback = this.updateCallback;
        this.updateCallback = null;
        this.close();
        callback(new Error('PasswordPrompt cancelled.') as unknown as string);
    }

    private showPasswordInputError(): void {
        if (!this.passwordInput) {
            return;
        }

        this.passwordInput.touched = true;
        this.passwordInput.valid = false;
        this.passwordInput.errorMessage = this.invalidMessage;
        this._changeDetectorRef.markForCheck();
    }

    private clearPasswordInputError(): void {
        if (!this.passwordInput) {
            return;
        }

        this.passwordInput.touched = false;
        this.passwordInput.valid = true;
        this.passwordInput.errorMessage = '';
        this._changeDetectorRef.markForCheck();
    }
}
